import {
  AiArtifactSchema,
  AiAttachmentUploadResponseSchema,
  AiConversationDetailSchema,
  AiConversationListResponseSchema,
  AiConversationSchema,
  AiMessageSchema,
  AiSkillListResponseSchema,
  AiUsageMeResponseSchema,
  ConfirmAiToolResponseSchema,
  type AiArtifact,
  type AiAttachmentRef,
  type AiConversation,
  type AiConversationDetail,
  type AiConversationListResponse,
  type AiMessage,
  type AiSkillId,
  type AiSkillListResponse,
  type AiSourceRef,
  type AiUsageMeResponse,
  type ConfirmAiTool,
  type ConfirmAiToolResponse,
  type CreateAiArtifact,
  type CreateAiConversation,
  type PostAiMessage,
} from "@l30/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import { apiFetch, apiFetchUnchecked, streamRequest } from "./client.js";

const AI_SKILLS_KEY = ["ai", "skills"] as const;
const AI_CONVERSATIONS_KEY = ["ai", "conversations"] as const;
const AI_USAGE_KEY = ["ai", "usage", "me"] as const;

function conversationKey(id: string) {
  return ["ai", "conversations", id] as const;
}

export function useAiSkills(enabled = true) {
  return useQuery({
    queryKey: AI_SKILLS_KEY,
    queryFn: () => apiFetch<AiSkillListResponse>("/ai/skills", {}, AiSkillListResponseSchema),
    enabled,
    retry: false,
  });
}

export function useAiConversations(enabled = true) {
  return useQuery({
    queryKey: AI_CONVERSATIONS_KEY,
    queryFn: () =>
      apiFetch<AiConversationListResponse>(
        "/ai/conversations",
        {},
        AiConversationListResponseSchema,
      ),
    enabled,
    retry: false,
  });
}

export function useAiConversation(id: string | null) {
  return useQuery({
    queryKey: conversationKey(id ?? ""),
    queryFn: () =>
      apiFetch<AiConversationDetail>(`/ai/conversations/${id}`, {}, AiConversationDetailSchema),
    enabled: Boolean(id),
    retry: false,
  });
}

export function useCreateAiConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAiConversation) =>
      apiFetch<AiConversation>(
        "/ai/conversations",
        { method: "POST", body: JSON.stringify(body) },
        AiConversationSchema,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AI_CONVERSATIONS_KEY });
    },
  });
}

export function useCreateAiArtifact() {
  return useMutation({
    mutationFn: (body: CreateAiArtifact) =>
      apiFetch<AiArtifact>(
        "/ai/artifacts",
        { method: "POST", body: JSON.stringify(body) },
        AiArtifactSchema,
      ),
  });
}

export function useAiUsageMe(enabled = true) {
  return useQuery({
    queryKey: AI_USAGE_KEY,
    queryFn: () => apiFetch<AiUsageMeResponse>("/ai/usage/me", {}, AiUsageMeResponseSchema),
    enabled,
    retry: false,
  });
}

const AI_BUDGET_KEY = ["ai", "budget"] as const;

export function useAiBudget(enabled = true) {
  return useQuery({
    queryKey: AI_BUDGET_KEY,
    queryFn: () =>
      apiFetchUnchecked<{
        settings: {
          tenantMonthlyTokenCap: number | null;
          defaultUserMonthlyTokenCap: number | null;
          updatedAt: string | null;
          updatedBy: string | null;
        };
        userBudgets: Array<{ userEmail: string; monthlyTokenCap: number; updatedAt: string }>;
        tenantUsage: {
          requestCount: number;
          inputTokens: number;
          outputTokens: number;
          uniqueUsers: number;
        };
        monthUsage: {
          periodStart: string;
          requestCount: number;
          inputTokens: number;
          outputTokens: number;
        };
      }>("/ai/budget"),
    enabled,
    retry: false,
  });
}

export function useSaveAiBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      tenantMonthlyTokenCap: number | null;
      defaultUserMonthlyTokenCap: number | null;
    }) =>
      apiFetchUnchecked("/ai/budget", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AI_BUDGET_KEY });
    },
  });
}

export function useSaveAiUserBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { userEmail: string; monthlyTokenCap: number | null }) =>
      apiFetchUnchecked("/ai/budget/users", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AI_BUDGET_KEY });
    },
  });
}

type AiStreamFrame =
  | { start: { conversationId: string; messageId: string; sources: unknown[] } }
  | { t: string }
  | { tool_use: { id: string; name: string } }
  | { tool_result: { id: string; name: string; isError?: boolean } }
  | {
      tool_pending: {
        toolId: string;
        args: Record<string, unknown>;
        confirmationToken: string;
      };
    }
  | { done: { messageId: string; generatedAt: string; citations?: AiChatTurn["citations"] } }
  | { error: { message: string } };

export type PendingConfirmTool = {
  toolId: string;
  args: Record<string, unknown>;
  confirmationToken: string;
};

function parseNdjsonChunk(chunk: string, carry: string): { frames: AiStreamFrame[]; rest: string } {
  const buf = carry + chunk;
  const parts = buf.split("\n");
  const rest = parts.pop() ?? "";
  const frames = parts.filter((l) => l.trim()).map((l) => JSON.parse(l) as AiStreamFrame);
  return { frames, rest };
}

export async function streamAiMessage(
  conversationId: string,
  body: PostAiMessage,
  on: {
    start?: (s: { conversationId: string; messageId: string }) => void;
    delta: (t: string) => void;
    toolUse?: (t: { id: string; name: string }) => void;
    toolResult?: (t: { id: string; name: string; isError?: boolean }) => void;
    toolPending?: (p: PendingConfirmTool) => void;
    done?: (d: {
      messageId: string;
      generatedAt: string;
      citations?: Array<{ sourceType: string; sourceId: string; title: string; excerpt?: string }>;
    }) => void;
    error?: (message: string) => void;
  },
  signal?: AbortSignal,
): Promise<void> {
  const res = await streamRequest(`/ai/conversations/${conversationId}/stream`, body, signal);
  if (!res.ok) {
    let message = `Insights AI stream failed (${res.status})`;
    try {
      const errBody = (await res.json()) as { error?: { message?: string } };
      if (errBody.error?.message) message = errBody.error.message;
    } catch {
      /* ignore */
    }
    on.error?.(message);
    throw new Error(message);
  }
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let carry = "";
  let terminalSeen = false;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    const { frames, rest } = parseNdjsonChunk(decoder.decode(value, { stream: true }), carry);
    carry = rest;
    for (const f of frames) {
      if ("start" in f) {
        on.start?.(f.start);
      } else if ("tool_use" in f) {
        on.toolUse?.(f.tool_use);
      } else if ("tool_result" in f) {
        on.toolResult?.(f.tool_result);
      } else if ("t" in f) {
        on.delta(f.t);
      } else if ("tool_pending" in f) {
        on.toolPending?.(f.tool_pending);
      } else if ("done" in f) {
        terminalSeen = true;
        on.done?.(f.done);
      } else if ("error" in f) {
        terminalSeen = true;
        on.error?.(f.error.message);
        throw new Error(f.error.message);
      }
    }
  }
  if (!terminalSeen) {
    throw new Error("Stream closed unexpectedly — please try again.");
  }
}

export interface AiChatTurn {
  role: "user" | "assistant";
  content: string;
  messageId?: string;
  citations?: Array<{ sourceType: string; sourceId: string; title: string; excerpt?: string }>;
}

export function useStreamingAiChat() {
  const qc = useQueryClient();
  const [messages, setMessages] = useState<AiChatTurn[]>([]);
  const [isStreaming, setStreaming] = useState(false);
  const [statusLabel, setStatusLabel] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [lastAssistantId, setLastAssistantId] = useState<string | null>(null);
  const [pendingConfirms, setPendingConfirms] = useState<PendingConfirmTool[]>([]);
  const abort = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    abort.current?.abort();
    setMessages([]);
    setConversationId(null);
    setLastAssistantId(null);
    setPendingConfirms([]);
    setStreaming(false);
    setStatusLabel(null);
  }, []);

  const hydrate = useCallback((detail: AiConversationDetail) => {
    setConversationId(detail.conversation.id);
    setMessages(
      detail.messages
        .filter((m) => m.role === "user" || m.role === "assistant")
        .map((m) => ({
          role: m.role as "user" | "assistant",
          content: m.content,
          messageId: m.id,
          citations: m.citations,
        })),
    );
    const lastAsst = [...detail.messages].reverse().find((m) => m.role === "assistant");
    setLastAssistantId(lastAsst?.id ?? null);
    setPendingConfirms([]);
    setStatusLabel(null);
  }, []);

  const ask = useCallback(
    (opts: {
      conversationId: string;
      content: string;
      sources?: AiSourceRef[];
      ragEnabled?: boolean;
      attachments?: AiAttachmentRef[];
      skillIds?: AiSkillId[];
    }) => {
      abort.current?.abort();
      const ctrl = new AbortController();
      abort.current = ctrl;
      setConversationId(opts.conversationId);
      setPendingConfirms([]);
      setStatusLabel("Thinking…");
      const attachNote =
        opts.attachments && opts.attachments.length > 0
          ? `\n\n[Attached: ${opts.attachments.map((a) => a.filename).join(", ")}]`
          : "";
      setMessages((m) => [
        ...m,
        { role: "user", content: `${opts.content}${attachNote}` },
        { role: "assistant", content: "" },
      ]);
      setStreaming(true);
      void streamAiMessage(
        opts.conversationId,
        {
          content: opts.content,
          sources: opts.sources,
          ragEnabled: opts.ragEnabled,
          attachments: opts.attachments,
          skillIds: opts.skillIds,
        },
        {
          toolUse: (tu) => {
            setStatusLabel(`Using ${tu.name.replace(/_/g, " ")}…`);
          },
          delta: (t) => {
            setStatusLabel(null);
            setMessages((m) => {
              const next = m.slice();
              const last = next[next.length - 1];
              if (last?.role === "assistant") {
                next[next.length - 1] = { ...last, content: last.content + t };
              }
              return next;
            });
          },
          toolPending: (p) => setPendingConfirms((prev) => [...prev, p]),
          done: (d) => {
            setStatusLabel(null);
            setLastAssistantId(d.messageId);
            setMessages((m) => {
              const next = m.slice();
              const last = next[next.length - 1];
              if (last?.role === "assistant") {
                next[next.length - 1] = {
                  ...last,
                  messageId: d.messageId,
                  citations: d.citations,
                };
              }
              return next;
            });
            void qc.invalidateQueries({ queryKey: AI_CONVERSATIONS_KEY });
            void qc.invalidateQueries({ queryKey: conversationKey(opts.conversationId) });
            void qc.invalidateQueries({ queryKey: AI_USAGE_KEY });
          },
          error: () => {
            setStatusLabel(null);
          },
        },
        ctrl.signal,
      )
        .catch(() => {
          setStatusLabel(null);
        })
        .finally(() => {
          setStreaming(false);
          setStatusLabel(null);
        });
    },
    [qc],
  );

  const cancel = useCallback(() => {
    abort.current?.abort();
    setStreaming(false);
    setStatusLabel(null);
  }, []);

  const clearPendingConfirms = useCallback(() => setPendingConfirms([]), []);

  return {
    messages,
    isStreaming,
    statusLabel,
    conversationId,
    lastAssistantId,
    pendingConfirms,
    ask,
    reset,
    hydrate,
    setConversationId,
    cancel,
    clearPendingConfirms,
  };
}

export function useConfirmAiTool() {
  return useMutation({
    mutationFn: (body: ConfirmAiTool) =>
      apiFetch<ConfirmAiToolResponse>(
        "/ai/tools/confirm",
        { method: "POST", body: JSON.stringify(body) },
        ConfirmAiToolResponseSchema,
      ),
  });
}

/** Best-effort audit of a dismissed confirmation; callers ignore failures. */
export async function cancelAiToolConfirmation(confirmationToken: string): Promise<void> {
  await apiFetchUnchecked<void>("/ai/tools/cancel", {
    method: "POST",
    body: JSON.stringify({ confirmationToken }),
  });
}

/** Non-streaming post — kept for contract/tests. */
export async function postAiMessage(
  conversationId: string,
  body: PostAiMessage,
): Promise<AiMessage> {
  return apiFetch(
    `/ai/conversations/${conversationId}/messages`,
    { method: "POST", body: JSON.stringify(body) },
    AiMessageSchema,
  );
}

/** Signed PUT upload for Ask Insights conversation attachments. */
export async function uploadAiAttachment(
  conversationId: string,
  file: File,
): Promise<AiAttachmentRef> {
  const contentType = file.type || "application/octet-stream";
  const signedRaw = await apiFetchUnchecked<unknown>("/ai/attachments/upload-url", {
    method: "POST",
    body: JSON.stringify({
      conversationId,
      filename: file.name,
      contentType,
      size: file.size,
    }),
  });
  const signed = AiAttachmentUploadResponseSchema.parse(signedRaw);
  const putContentType = signed.contentType;
  const putRes = await fetch(signed.uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": putContentType,
      "x-ms-blob-type": "BlockBlob",
    },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`AI attachment upload failed: ${putRes.status}`);
  }
  return {
    key: signed.key,
    filename: file.name,
    contentType: putContentType,
    size: file.size,
  };
}
